import { isMiniApp } from './platform';
import { OpenZaloForSubmission, useZaloSubmission } from './zalo-submission';
import { useRef, useState, type FormEvent } from 'react';
import type { AdmissionContext } from '@ulaw/content-types/admissions';
import {
  WIZARD_ADVICE_CONSENT,
  WIZARD_ADVICE_CONSENT_VERSION,
  WIZARD_ADVICE_FORM_VERSION,
  type WizardAdviceInput,
  type WizardAdviceReceipt,
} from '@ulaw/content-types/admissions-advice';
import { normalizeConsultationPhone } from '@ulaw/content-types/phone';
import { endpoint, privacyUrl } from './content';

export function WizardAdvice({
  context,
  answers,
  sourceIssueIds,
  answerLabels,
  onReceived,
}: {
  answerLabels: Record<string, string>;
  context: AdmissionContext;
  answers: Record<string, unknown>;
  sourceIssueIds: string[];
  onReceived: () => void;
}) {
  const [open, setOpen] = useState(false);
  const zalo = useZaloSubmission();
  const [fullname, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [school, setSchool] = useState('');
  const [question, setQuestion] = useState('');
  const [consent, setConsent] = useState(false);
  const [shareKeys, setShareKeys] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState<WizardAdviceReceipt>();
  const [showSent, setShowSent] = useState(false);
  const pending = useRef<{ body: string; id: string }>();
  const sending = useRef(false);
  const keys = Object.keys(answers).filter(
    (key) => answers[key] !== '' && answers[key] !== undefined
  );
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (sending.current) return;
    const normalizedPhone = normalizeConsultationPhone(phone);
    if (!fullname.trim() || !normalizedPhone || !consent) {
      setError('Cần họ tên, số điện thoại hợp lệ và đồng ý xử lý thông tin cho yêu cầu tư vấn.');
      return;
    }
    const selected = Object.fromEntries(
      shareKeys.filter((key) => keys.includes(key)).map((key) => [key, answers[key]])
    );
    const payload: WizardAdviceInput = {
      formVersion: WIZARD_ADVICE_FORM_VERSION,
      consentVersion: WIZARD_ADVICE_CONSENT_VERSION,
      fullname: fullname.trim(),
      phone: normalizedPhone,
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(school.trim() ? { school: school.trim() } : {}),
      question: question.trim(),
      context,
      sourceIssueIds,
      consent: true,
      ...(Object.keys(selected).length ? { shareAnswers: true, sharedAnswers: selected } : {}),
    };
    const body = JSON.stringify(payload);
    if (pending.current?.body !== body) pending.current = { body, id: crypto.randomUUID() };
    sending.current = true;
    setBusy(true);
    setError('');
    try {
      const identity = await zalo.grant();
      const response = await fetch(endpoint('/api/admissions/consultations'), {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': pending.current.id },
        body: JSON.stringify({ ...JSON.parse(pending.current.body), ...identity }),
        signal: AbortSignal.timeout(30_000),
      });
      const result = await response.json();
      if (!response.ok || result.error)
        throw new Error(result.error?.message || 'Chưa gửi được yêu cầu. Vui lòng thử lại.');
      setReceipt(result as WizardAdviceReceipt);
      onReceived();
    } catch {
      setError(
        'Chưa xác nhận được việc tiếp nhận. Bạn có thể gửi lại; nội dung không đổi sẽ dùng lại cùng mã gửi.'
      );
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }
  const sent =
    receipt && pending.current
      ? (JSON.parse(pending.current.body) as WizardAdviceInput)
      : undefined;
  if (receipt)
    return (
      <section className="wizard-advice" aria-live="polite">
        <h2>Đã tiếp nhận yêu cầu</h2>
        <p>
          Mã yêu cầu: <strong>{receipt.id}</strong>
        </p>
        <p>Yêu cầu đang chờ xử lý.</p>
        <p>Đây là yêu cầu tư vấn, không phải hồ sơ xét tuyển.</p>
        <button type="button" className="wizard-secondary" onClick={() => setShowSent(!showSent)}>
          Xem lại nội dung đã gửi
        </button>
        {showSent && pending.current && (
          <div className="wizard-sent">
            <p>{sent?.question || 'Không có câu hỏi bổ sung.'}</p>
            <p>
              Năm: {sent?.context.admissionYear} · Ngành:{' '}
              {sent?.context.interestedMajorIds.join(', ') || 'Chưa chọn ngành'}
            </p>
            <p>
              {sent?.sharedAnswers
                ? `Đã chọn chia sẻ ${Object.keys(sent.sharedAnswers).length} nhóm dữ kiện.`
                : 'Không chia sẻ điểm hoặc câu trả lời đã nhập.'}
            </p>
          </div>
        )}
      </section>
    );
  if (!isMiniApp()) return <OpenZaloForSubmission/>;
  return (
    <section className="wizard-advice">
      <h2>Cần trao đổi thêm?</h2>
      {!open ? (
        <>
          <p>Chủ động gửi câu hỏi và chọn thông tin muốn chia sẻ với bộ phận tư vấn.</p>
          <button type="button" className="wizard-secondary" onClick={() => setOpen(true)}>
            Nhờ tư vấn
          </button>
        </>
      ) : (
        <form onSubmit={submit}>
          <h3>Gửi yêu cầu tư vấn</h3>
          <p className="wizard-notice">
            Ngữ cảnh gửi: {context.admissionYear} ·{' '}
            {context.degreeGoal === 'first_degree'
              ? 'Đại học'
              : context.degreeGoal === 'second_degree'
                ? 'Văn bằng hai'
                : 'Sau đại học'}{' '}
            · {context.campusId || 'Chưa chọn nơi học'} ·{' '}
            {context.interestedMajorIds.join(', ') || 'Chưa rõ ngành'}. Bạn có thể quay lại sửa lựa
            chọn trước khi gửi.
          </p>
          <fieldset disabled={busy}>
            <div className="wizard-field">
              <label htmlFor="advice-name">Họ tên *</label>
              <input
                id="advice-name"
                value={fullname}
                onChange={(event) => setName(event.target.value)}
                required
                maxLength={120}
                autoComplete="name"
              />
            </div>
            <div className="wizard-field">
              <label htmlFor="advice-phone">Số điện thoại *</label>
              <input
                id="advice-phone"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                required
                autoComplete="tel"
                maxLength={20}
              />
            </div>
            <div className="wizard-field">
              <label htmlFor="advice-email">Email (không bắt buộc)</label>
              <input
                id="advice-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                maxLength={254}
              />
            </div>
            <div className="wizard-field">
              <label htmlFor="advice-school">Trường đang học (không bắt buộc)</label>
              <input
                id="advice-school"
                value={school}
                onChange={(event) => setSchool(event.target.value)}
                maxLength={200}
              />
            </div>
            <div className="wizard-field">
              <label htmlFor="advice-question">Câu hỏi / nội dung muốn trao đổi</label>
              <textarea
                id="advice-question"
                rows={4}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                maxLength={2000}
              />
            </div>
            {keys.length > 0 && (
              <fieldset>
                <legend>Chia sẻ dữ kiện đã nhập (không bắt buộc)</legend>
                <p className="muted">Mặc định không chia sẻ. Chỉ dữ kiện bạn chọn mới được gửi.</p>
                {keys.map((key) => (
                  <label className="wizard-option" key={key}>
                    <input
                      type="checkbox"
                      checked={shareKeys.includes(key)}
                      onChange={(event) =>
                        setShareKeys(
                          event.target.checked
                            ? [...shareKeys, key]
                            : shareKeys.filter((item) => item !== key)
                        )
                      }
                    />
                    <span>
                      {Array.isArray(answers[key])
                        ? (answers[key] as unknown[])
                            .map(
                              (value, index) =>
                                `${answerLabels[`${key}.${index}`] || `Môn ${index + 1}`}: ${String(value)}`
                            )
                            .join(' · ')
                        : `${answerLabels[key] || 'Dữ kiện đã nhập'}: ${String(answers[key])}`}
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
            <label className="wizard-option">
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
                required
              />
              <span>{WIZARD_ADVICE_CONSENT}</span>
            </label>
            <a href={privacyUrl()} target="_blank" rel="noopener noreferrer">
              Xem chính sách dữ liệu
            </a>
            {error && (
              <p role="alert" className="wizard-notice wizard-error">
                {error}
              </p>
            )}
            <button type="submit" className="wizard-primary" disabled={busy || !consent}>
              {busy ? 'Đang gửi…' : error ? 'Gửi lại yêu cầu' : 'Gửi yêu cầu tư vấn'}
            </button>
            <button
              type="button"
              className="wizard-secondary"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Đóng biểu mẫu
            </button>
          </fieldset>
        </form>
      )}
    </section>
  );
}
